import type { CSSProperties } from 'react';
import { z } from 'zod';
import { Pin } from '@/components/Pin';
import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetData } from '@/hooks/useWidgetData';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import { assetUrl, IMG } from '@/lib/assets';
import { fontOptionsSchema } from '@/lib/fontStyle';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';

/**
 * A map image with labelled pins on it, each optionally showing a live travel time. Port of
 * `travelWidget/travelWidget.vue`.
 *
 * **No deployed config uses this widget** — it is one of the three types the legacy app
 * registered and none of the seven configs carries (`src/widgets/knownWidgetTypes.ts`). It is
 * ported because a config can start using it without a code change, and it is verified against
 * `public/static/__travel-test.json`, which exists for exactly that purpose and is not a
 * display.
 *
 * ## Pins are matched to travel times **by position**
 *
 * `payload.travelTimes[i]` belongs to the `i`-th **non-static** pin, in config order. Nothing
 * in either the config or the payload names the pin a time belongs to, so the two arrays are
 * coupled by index alone: inserting a pin in the middle of `options.pins`, or marking one
 * `static`, silently renumbers every pin after it. That is the legacy contract and it cannot be
 * changed without changing whatever publishes the topic — it is written down here because it is
 * invisible in both files and would be very hard to diagnose from a screenshot of a wall.
 *
 * Static pins are landmarks: they render with no travel time and take no slot in the array.
 *
 * ## Two legacy behaviours deliberately not carried over
 *
 * - **W-11.** `travelWidget.vue` assigns `pin.position`, `pin.static` and the split lists onto
 *   the *config objects* while building them. The config is shared and, under SYM-492's slider,
 *   re-read on every slide change; positions are derived here instead.
 * - **The last-good latch.** Legacy kept the previous travel times when a message arrived
 *   without a `travelTimes` array (`watch` guarded on it). This widget derives from the latest
 *   payload like every other widget in the app, so such a message returns the pins to their
 *   no-time state (docs/MIGRATION-WIDGETS.md §9) rather than showing times that are no longer
 *   being published. No deployed config is affected; recorded in `docs/reports/SYM-501.md`.
 */

const pinSchema = z.object({
  title: z.string().optional(),
  /** One of `PinDirections`; anything else falls back to `DOWN` inside `Pin`. */
  arrow: z.string().optional(),
  /** A file under `src/assets/img/icons/`. Never set by a legacy config — see below. */
  icon: z.string().optional(),
  /** Percent offsets within the map container. */
  top: z.number().optional(),
  left: z.number().optional(),
  /** A landmark: rendered without a travel time and skipped when matching the payload. */
  static: z.boolean().optional(),
});

const optionsSchema = z.object({
  topic: z.string().optional(),
  layout: widgetLayoutSchema.optional(),
  map: z
    .object({
      /** Resolved under `src/assets/img/`, like `ImageLabelWidget`. */
      img: z.string().default(''),
      /** px */
      borderRadius: z.number().optional(),
      /** rem */
      maxHeight: z.number().optional(),
    })
    .prefault({}),
  /** Shared by every pin: the label box background and the font inside it. */
  pinStyle: z
    .object({ color: z.string().optional(), font: fontOptionsSchema.optional() })
    .prefault({}),
  pins: z.array(pinSchema).prefault([]),
});

/** docs/MIGRATION-WIDGETS.md §7 — minutes, positionally matched to the non-static pins. */
const payloadSchema = z.looseObject({ travelTimes: z.array(z.number()).optional() });

const MAP_IMAGE_STYLE: CSSProperties = { width: '100%', height: '100%', objectFit: 'fill' };

export function TravelWidget({ componentId, options }: WidgetProps) {
  const parsed = optionsSchema.safeParse(options);
  // Hooks run before any early return.
  const opts = parsed.success ? parsed.data : undefined;
  const data = useWidgetData<unknown>(componentId, opts?.topic);
  const { className, style } = useWidgetLayout(opts?.layout);

  if (!parsed.success) {
    warnOnce(
      `travel-widget-options:${componentId}`,
      '[signage] TravelWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  const opt = parsed.data;
  const travelTimes = readTravelTimes(data, componentId);

  // Derived, never assigned back onto the config (W-11). The two lists keep config order,
  // which is what makes the index coupling above well defined.
  const livePins = opt.pins.filter((pin) => !pin.static);
  const staticPins = opt.pins.filter((pin) => pin.static);

  const mapUrl = opt.map.img ? assetUrl(`${IMG.root}${opt.map.img}`) : undefined;

  const containerStyle: CSSProperties = {
    position: 'relative',
    width: '100%',
    maxHeight: opt.map.maxHeight ? `${String(opt.map.maxHeight)}rem` : 'none',
    overflow: 'hidden',
    borderRadius: opt.map.borderRadius ? `${String(opt.map.borderRadius)}px` : '0px',
  };

  return (
    <div className={className} style={style}>
      <div className="travel-container" style={containerStyle}>
        {/*
         * A missing file renders no image rather than a broken one — `assetUrl` warns once.
         * The legacy `require()` *threw* here, which took the whole page down.
         */}
        {mapUrl !== undefined && <img src={mapUrl} alt="" style={MAP_IMAGE_STYLE} />}
        {livePins.map((pin, index) => (
          <Pin
            key={index}
            title={pin.title}
            // The index coupling, in one line: the i-th non-static pin, the i-th travel time.
            // Absent or `0` renders the title alone (docs/MIGRATION-WIDGETS.md §9).
            subtitle={travelTimes?.[index] ?? 0}
            icon={pin.icon}
            pinDirection={pin.arrow}
            pinStyle={opt.pinStyle}
            position={{ top: pin.top, left: pin.left }}
          />
        ))}
        {staticPins.map((pin, index) => (
          <Pin
            key={index}
            title={pin.title}
            icon={pin.icon}
            pinDirection={pin.arrow}
            pinStyle={opt.pinStyle}
            position={{ top: pin.top, left: pin.left }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * `payload.travelTimes`, or `undefined` before the first message / on a shape mismatch.
 *
 * `undefined` and an array of zeros render identically — a pin with no travel time — so the
 * empty state costs no extra branch at the call site.
 */
function readTravelTimes(data: unknown, componentId: string): number[] | undefined {
  if (data === undefined) return undefined;

  const parsed = payloadSchema.safeParse(data);
  if (!parsed.success) {
    warnOnce(
      `travel-widget-payload:${componentId}`,
      '[signage] TravelWidget payload rejected; pins keep their empty travel time. %s',
      z.prettifyError(parsed.error),
    );
    return undefined;
  }
  return parsed.data.travelTimes;
}
